import React, { createContext, useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import {
  getConversationDocument,
  getCurrentConversation,
  getDocumentById,
  getActiveDocumentId,
} from '../reducers/aiAssistant.selectors';
import { useSelector, useDispatch } from 'react-redux';
import {
  getGeneratedDocument,
  setActiveDocumentId,
  clearActiveDocument,
  updateGeneratedPodcast,
} from '../actions/aiAssistant.actions';
import { debounce } from 'debounce';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import { useTranslation } from 'react-i18next';
import { aiAssistantContext } from './AIAssistantContextProvider';
import { Editor } from '@tiptap/react';
import { ActiveView, TITLE_SAVE_DEBOUNCE_MS } from '../components/AIAssistant/shared/constants';
import { convertMarkdownToHtml } from '../components/utils/contentConverter';
import useRouter from '../utils/useRouter';
import { routes } from '../constants';
import services from '../services';
import { TemplatePrompts } from '@klab-berlin/api-sdk/lib/types/common/Conversation';
import { GeneratedPodcastStageStatus } from '@klab-berlin/api-sdk/lib/types/responses/Ai';
import { UpdateGeneratedPodcast } from '@klab-berlin/api-sdk/lib/types/requests/Ai';
import { notifyBugsnagNetwork } from '../utils/bugsnagClient';
import {
  podcastFormatMap, podcastLanguageMap
} from '../components/AIAssistant/shared/TemplatePromptForm/AIAssistantTemplatePromptForm';

export enum AIAssistantEditorMode {
    PLAYGROUND = 'PLAYGROUND',
    STREAM = 'STREAM',
    PREVIOUS = 'PREVIOUS',
    PODCAST = 'PODCAST',
}

export interface TextEditorData {
  id: string | null;
  title: string;
  content: string;
}

export interface PodcastDetailsData {
  id: string;
  title: string;
  subject: string;
  grade: string;
  language: string;
  format: string;
  transcript: string;
  audioUrl?: string;
  durationLabel?: string;
  audioGenerationFailed?: boolean;
}

export interface EditorCloseScrollTarget {
  messageId: string | null;
  documentId: string | null;
}

export interface AIAssistantEditorContext {
    isTextEditorOpen: boolean;
    mode: AIAssistantEditorMode;
    openTextEditor: (options?: { messageId?: string; documentId?: string }) => void;
    openPodcastDetails: (podcast: PodcastDetailsData) => void;
    podcastDetails: PodcastDetailsData | null;
    closeTextEditor: () => void;
    messageId: string | null;
    documentId: string | null;
    editorCloseScrollTarget: EditorCloseScrollTarget | null;
    clearEditorCloseScrollTarget: () => void;
    activeDocumentCardRef: React.RefObject<HTMLDivElement>;
    activeImageRef: React.RefObject<HTMLDivElement>;
    imageScrollTarget: string | null;
    clearImageScrollTarget: () => void;
    editorData: TextEditorData;
    setTextEditorContent: (content: string) => void;
    textEditorRef: React.MutableRefObject<Editor | null> | null;
    hasUnsavedChanges: boolean;
    dismissChangeIndicator: () => void;
    markContentChanged: () => void;
    markTitleChanged: () => void;
    updatePodcastTitle: (title: UpdateGeneratedPodcast['title']) => void;
    flushPodcastTitleSave: () => void;
}

export const aiAssistantEditorContext = createContext<AIAssistantEditorContext>({
  isTextEditorOpen: false,
  mode: AIAssistantEditorMode.PLAYGROUND,
  openTextEditor: () => {},
  openPodcastDetails: () => {},
  podcastDetails: null,
  closeTextEditor: () => {},
  messageId: null,
  documentId: null,
  editorCloseScrollTarget: null,
  clearEditorCloseScrollTarget: () => {},
  activeDocumentCardRef: { current: null },
  activeImageRef: { current: null },
  imageScrollTarget: null,
  clearImageScrollTarget: () => {},
  editorData: {
    id: null,
    title: '',
    content: '',
  },
  setTextEditorContent: () => {},
  textEditorRef: null,
  hasUnsavedChanges: false,
  dismissChangeIndicator: () => {},
  markContentChanged: () => {},
  markTitleChanged: () => {},
  updatePodcastTitle: () => {},
  flushPodcastTitleSave: () => {},
});

interface AIAssistantEditorProviderProps {
  variant?: 'page' | 'dialog';
}

const AIAssistantEditorProvider: React.FC<AIAssistantEditorProviderProps> = ({ children, variant = 'page' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    setIsExtended, isAssistantOpen, activeView, isExtended, readyPodcastId, clearPendingPodcastDetails,
    pendingPodcastIdRef,
  } = useContext(aiAssistantContext);
  const { router, match: { location: { pathname, query } } } = useRouter();
  const [isTextEditorOpen, setIsTextEditorOpen] = useState(false);
  const [mode, setMode] = useState<AIAssistantEditorMode>(AIAssistantEditorMode.PLAYGROUND);
  const [messageId, setMessageId] = useState<string | null>(null);
  const textEditorRef = useRef<Editor | null>(null);
  const lastResolvedDataRef = useRef<TextEditorData | null>(null);
  const pendingDocumentIdRef = useRef<string | null>(null);
  const shouldRetryRef = useRef(false);
  const documentId = useSelector(getActiveDocumentId);
  const streamDocument = useSelector(getConversationDocument);
  const effectiveDocumentId = pendingDocumentIdRef.current || documentId;
  const historyDocument = useSelector((state) =>
    effectiveDocumentId ? getDocumentById(state, effectiveDocumentId) : undefined
  );
  const currentConversation = useSelector(getCurrentConversation);
  const [editorData, setEditorData] = useState<TextEditorData>({
    id: null,
    title: '',
    content: '',
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [podcastDetails, setPodcastDetails] = useState<PodcastDetailsData | null>(null);
  const podcastModeRef = useRef(false);
  const originalDataRef = useRef<TextEditorData | null>(null);
  const [editorCloseScrollTarget, setEditorCloseScrollTarget] = useState<EditorCloseScrollTarget | null>(null);
  const activeDocumentCardRef = useRef<HTMLDivElement>(null);
  const activeImageRef = useRef<HTMLDivElement>(null);
  const [imageScrollTarget, setImageScrollTarget] = useState<string | null>(null);
  const clearEditorCloseScrollTarget = useCallback(() => setEditorCloseScrollTarget(null), []);
  const clearImageScrollTarget = useCallback(() => setImageScrollTarget(null), []);
  const lastPodcastConversationIdRef = useRef<string | null>(null);

  const setTextEditorContent = (content: string) => {
    setEditorData({
      ...editorData,
      content,
    });
  };

  const markContentChanged = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  const markTitleChanged = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  const debouncedPodcastSave = useRef(
    debounce((podcastId: string, title: string) => {
      dispatch(updateGeneratedPodcast({ podcastId, title }));
    }, TITLE_SAVE_DEBOUNCE_MS)
  ).current;

  // Optimistically update the local title, then debounce-persist — mirrors handleTitleChange for documents.
  const updatePodcastTitle = useCallback((title: UpdateGeneratedPodcast['title']) => {
    setPodcastDetails((prev) => (prev ? { ...prev, title } : prev));
    if (podcastDetails?.id) {
      debouncedPodcastSave(podcastDetails.id, title);
    }
  }, [podcastDetails?.id, debouncedPodcastSave]);

  // Persists a pending debounced title save immediately.
  const flushPodcastTitleSave = useCallback(() => {
    debouncedPodcastSave.flush?.();
  }, [debouncedPodcastSave]);

  const dismissChangeIndicator = useCallback(() => {
    setHasUnsavedChanges(false);
    originalDataRef.current = {
      ...editorData,
    };
  }, [editorData]);

  const openTextEditor = (options?: { messageId?: string; documentId?: string }) => {
    podcastModeRef.current = false;
    setIsExtended(true);
    if (options?.messageId || options?.documentId) {
      setEditorCloseScrollTarget({
        messageId: options?.messageId ?? null,
        documentId: options?.documentId ?? null,
      });
    }
    if (options?.messageId) {
      setMode(AIAssistantEditorMode.PLAYGROUND);
      setMessageId(options.messageId);
      pendingDocumentIdRef.current = null;
      dispatch(clearActiveDocument());
    } else if (options?.documentId) {
      setMode(AIAssistantEditorMode.PREVIOUS);
      pendingDocumentIdRef.current = options.documentId;
      shouldRetryRef.current = true;
      dispatch(setActiveDocumentId({ documentId: options.documentId }));
      setMessageId(null);
    } else {
      setMode(AIAssistantEditorMode.STREAM);
      setMessageId(null);
      pendingDocumentIdRef.current = null;
      dispatch(clearActiveDocument());
    }
    // Don't set isTextEditorOpen here - let the effect on line 233 handle it
    // once we have actual content to display (line 248-256)
  };

  const openPodcastDetails = (podcast: PodcastDetailsData) => {
    podcastModeRef.current = true;
    setIsExtended(true);
    setMode(AIAssistantEditorMode.PODCAST);
    setMessageId(null);
    pendingDocumentIdRef.current = null;
    dispatch(clearActiveDocument());
    setPodcastDetails(podcast);
    setEditorData({ id: null, title: podcast.title, content: '' });
    setIsTextEditorOpen(true);
  };

  const closeTextEditor = useCallback(() => {
    // editorCloseScrollTarget is intentionally not cleared here.
    // useAIAssistantAutoScroll() hooks reads it after closeTextEditor() 
    // to scroll to the correct position, then clears it via clearEditorCloseScrollTarget().
    setIsTextEditorOpen(false);
    setMessageId(null);
    setEditorData({
      id: null,
      title: '',
      content: '',
    });
    lastResolvedDataRef.current = null;
    pendingDocumentIdRef.current = null;
    shouldRetryRef.current = false;
    setMode(AIAssistantEditorMode.PLAYGROUND);
    setIsExtended(false);
    setHasUnsavedChanges(false);
    podcastModeRef.current = false;
    setPodcastDetails(null);
    originalDataRef.current = null;
    clearActiveDocument();

    if (query?.edit || query?.id) {
      router.replace(pathname);
    }
  }, [setIsTextEditorOpen, setMessageId, setEditorData, setMode, setIsExtended, query, router, pathname]);

  const isEditModeFromUrl = query?.edit === 'true' && !!query?.id;
  const imageIdFromUrl = query?.imageId;

  // The always-mounted dialog instance must not react to state owned by the page instance while closed.
  const isDormant = variant === 'dialog' && !isAssistantOpen;

  useEffect(() => {
    if (isDormant) return;
    const isAIToolsPage = pathname.startsWith(`/${routes.aiTools.root}`);

    if (isAIToolsPage && isEditModeFromUrl && activeView === ActiveView.CHAT) {
      openTextEditor({ documentId: query.id });
    }
  }, [pathname, activeView, isEditModeFromUrl, query?.id, isDormant]);

  // Auto-open podcast details when a podcast conversation becomes active.
  // readyPodcastId is set by the polling effect after generation completes;
  // the message context fallback handles page refreshes.
  useEffect(() => {
    if (isDormant) return;
    // Already showing podcast details, no need to re-open.
    if (mode === AIAssistantEditorMode.PODCAST) return;
    // Only open after transitioning from PODCAST_LOADING to CHAT.
    if (activeView !== ActiveView.CHAT) return;
    // An explicit document-edit URL wins over podcast auto-open.
    if (isEditModeFromUrl) return;

    const messages = currentConversation?.data?.messages;
    const conversationId = currentConversation?.data?.id;
    if (!messages || !conversationId) return;
    if (lastPodcastConversationIdRef.current === conversationId) return;

    const podcastId = pendingPodcastIdRef.current
      || readyPodcastId
      || (messages.find(
        (msg) => msg.role === 'assistant' && (msg.context as any)?.generatedPodcastId
      )?.context as any)?.generatedPodcastId as string | undefined;
    if (!podcastId) return;

    const userMessage = messages.find(
      (msg) => msg.role === 'user' && msg.context?.templatePromptId === TemplatePrompts.CREATE_PODCAST
    );
    const params = userMessage?.context?.data;

    const reverseFormatMap = Object.fromEntries(
      Object.entries(podcastFormatMap).map(([label, value]) => [value, label])
    );
    const reverseLanguageMap = Object.fromEntries(
      Object.entries(podcastLanguageMap).map(([label, value]) => [value, label])
    );

    let cancelled = false;

    services.ai.getGeneratedPodcast(podcastId, { includeScriptText: true })
      .then((podcast) => {
        // User navigated away before fetch completed — discard result.
        if (cancelled) return;

        // skipping the ref mark lets the effect retry after the poll settles
        if (podcast.status === GeneratedPodcastStageStatus.LOADING) {
          clearPendingPodcastDetails();
          return;
        }

        // Mark only on success so a transient failure can retry.
        lastPodcastConversationIdRef.current = conversationId;

        const durationSec = podcast.audio?.durationSec;
        const durationLabel = durationSec
          ? `${Math.floor(durationSec / 60)}:${String(durationSec % 60).padStart(2, '0')}`
          : undefined;
        const formatValue = String(params?.format ?? '');
        const languageValue = String(params?.language ?? '');

        openPodcastDetails({
          id: podcastId,
          title: podcast.title,
          subject: String(params?.subject ?? ''),
          grade: params?.gradeLevel ? `${params.gradeLevel}. ${t('Class year')}` : '',
          language: t(reverseLanguageMap[languageValue] ?? languageValue.toUpperCase()),
          format: t(reverseFormatMap[formatValue] ?? formatValue),
          transcript: podcast.script?.content ?? '',
          audioUrl: podcast.audio?.url,
          durationLabel,
          audioGenerationFailed: podcast.status === GeneratedPodcastStageStatus.ERROR,
        });
        clearPendingPodcastDetails();
      })
      .catch((error) => {
        if (cancelled) return;
        clearPendingPodcastDetails();
        notifyBugsnagNetwork('AIAssistant', 'fetchPodcastDetails', { podcastId, error });
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentConversation?.data?.messages, currentConversation?.data?.id,
    mode, activeView, readyPodcastId, isDormant, isEditModeFromUrl,
  ]);

  // Close the podcast detail when the active conversation changes, so it doesn't
  // linger after navigating away via routes that don't call closeTextEditor()
  // (e.g. the sidebar link, which keeps this page-scoped provider mounted).
  const activeConversationId = currentConversation?.data?.id ?? null;
  useEffect(() => {
    if (isDormant) return;
    if (
      podcastModeRef.current &&
      lastPodcastConversationIdRef.current &&
      activeConversationId !== lastPodcastConversationIdRef.current
    ) {
      lastPodcastConversationIdRef.current = null;
      closeTextEditor();
    }
  }, [activeConversationId, closeTextEditor, isDormant]);

  useEffect(() => {
    if (!imageIdFromUrl) return;

    const messages = currentConversation?.data?.messages;
    if (!messages) return;

    const targetMessage = messages.find(
      (msg) => msg.context?.generatedImageId === imageIdFromUrl
    );

    if (targetMessage?.id) {
      setImageScrollTarget(targetMessage.id);
      router.replace(pathname);
    }
  }, [imageIdFromUrl, currentConversation?.data?.messages, pathname, router]);

  const resolveEditorData = useCallback(() => {
    const defaultData = {
      id: null,
      title: '',
      content: '',
      rawContent: '',
    };

    switch (mode) {
      case AIAssistantEditorMode.PLAYGROUND: {
        if (!messageId || !currentConversation?.data?.messages) return defaultData;
        let messageIndex = 0;   
        const foundMessage = currentConversation?.data?.messages.find(
          (message, index) => {
            if (message.id === messageId) {
              messageIndex = index;
              return true;
            }
            return false;
          }
        );
        if (!foundMessage) return defaultData;

        const previousUserMessage = currentConversation?.data?.messages[messageIndex - 1].content[0]?.text ?? '';
        const assistantMessage = foundMessage.content[0]?.text ?? '';

        return {
          id: null,
          title: t('Answer for:') + ' ' + previousUserMessage,
          content: '',
          rawContent: assistantMessage,
        };
      }
      case AIAssistantEditorMode.PREVIOUS: {
        if (!historyDocument?.data) return defaultData;
        return {
          id: historyDocument.data.id,
          title: historyDocument.data.title,
          content: '', 
          rawContent: historyDocument.data.content,
        };
      }
      case AIAssistantEditorMode.STREAM: {
        if (!streamDocument) return defaultData;
        return {
          id: streamDocument.id,
          title: streamDocument.title,
          content: '',
          rawContent: streamDocument.content,
        };
      }
      case AIAssistantEditorMode.PODCAST: {
        if (!podcastDetails) return defaultData;
        return {
          id: null,
          title: podcastDetails.title,
          content: '',
          rawContent: '',
        };
      }
      default:
        return defaultData;
    }
  }, [mode, messageId, streamDocument, historyDocument, currentConversation, podcastDetails, t]);

  // Memoize markdown-to-HTML conversion to only happen when text editor is actually open
  // This prevents expensive conversions during streaming when the editor is not visible
  const convertedHtmlContent = useMemo(() => {
    const rawData = resolveEditorData();
    if (!rawData.rawContent) {
      return '';
    }
    return convertMarkdownToHtml(rawData.rawContent);
  }, [resolveEditorData]);

  useEffect(() => {
    if (mode === AIAssistantEditorMode.PREVIOUS && effectiveDocumentId) {
      const documentStatus = historyDocument?.status;
      const isNotReady = !documentStatus ||
        (documentStatus !== AsyncActionStatus.READY &&
         documentStatus !== AsyncActionStatus.LOADING &&
         documentStatus !== AsyncActionStatus.ERROR);
      const shouldRetry = documentStatus === AsyncActionStatus.ERROR && shouldRetryRef.current;

      if (isNotReady || shouldRetry) {
        shouldRetryRef.current = false;
        dispatch(getGeneratedDocument({ documentId: effectiveDocumentId }));
      }
      // Clear pending ref once Redux has caught up
      if (pendingDocumentIdRef.current && documentId === pendingDocumentIdRef.current) {
        pendingDocumentIdRef.current = null;
      }
    }
  }, [mode, effectiveDocumentId, documentId, historyDocument?.status, dispatch]);

  useEffect(() => {
    const data = resolveEditorData();
    const dataWithHtml = {
      ...data,
      content: convertedHtmlContent,
    };

    const lastData = lastResolvedDataRef.current;
    if (!lastData ||
        dataWithHtml.id !== lastData.id ||
        dataWithHtml.title !== lastData.title ||
        dataWithHtml.content !== lastData.content) {
      lastResolvedDataRef.current = dataWithHtml;
      setEditorData(dataWithHtml);
      originalDataRef.current = { ...dataWithHtml };
      setHasUnsavedChanges(false);

      // Check if we have content OR if we have a title (indicating data was resolved)
      const hasValidData = dataWithHtml.content || dataWithHtml.title;
      if (hasValidData) {
        setIsTextEditorOpen(true);
        setIsExtended(true);
      }
    }
  }, [resolveEditorData, convertedHtmlContent, setIsExtended]);

  useEffect(() => {
    if (
      !isAssistantOpen &&
      !isEditModeFromUrl &&
      !imageIdFromUrl &&
      !podcastModeRef.current
    ) {
      closeTextEditor();
    }
  }, [isAssistantOpen, isEditModeFromUrl, imageIdFromUrl, closeTextEditor]);

  useEffect(() => {
    if (activeView !== ActiveView.CHAT && !podcastModeRef.current) {
      setIsTextEditorOpen(false);
      setMessageId(null);
      setEditorData({
        id: null,
        title: '',
        content: '',
      });
      setMode(AIAssistantEditorMode.PLAYGROUND);
      clearActiveDocument();
    }
  }, [activeView]);

  useEffect(() => {
    if (
      !isExtended &&
      isTextEditorOpen &&
      !isEditModeFromUrl &&
      !podcastModeRef.current
    ) {
      closeTextEditor();
    }
  }, [isTextEditorOpen, isExtended, isEditModeFromUrl, closeTextEditor]);

  return (
    <aiAssistantEditorContext.Provider value={{
      isTextEditorOpen,
      mode,
      openTextEditor,
      openPodcastDetails,
      podcastDetails,
      closeTextEditor,
      messageId,
      documentId,
      editorCloseScrollTarget,
      clearEditorCloseScrollTarget,
      activeDocumentCardRef,
      activeImageRef,
      imageScrollTarget,
      clearImageScrollTarget,
      editorData,
      setTextEditorContent,
      textEditorRef,
      hasUnsavedChanges,
      dismissChangeIndicator,
      markContentChanged,
      markTitleChanged,
      updatePodcastTitle,
      flushPodcastTitleSave,
    }}>
      {children}
    </aiAssistantEditorContext.Provider>
  );
};

export default AIAssistantEditorProvider;
